import { FC } from 'react';

import { ScenarioHooks, useLocale } from '../../../../../hooks/index';
import { toRound } from '../../../../../utils/get-rounded-number.util';
import { calcTotal } from '../../../../../utils/reduce-utils.util';
import { getScenarioGrantedPoolFigures } from '../../../../../utils/scenario-granted-pool-row.util';
import { toUpperCaseWords } from '../../../../../utils/to-uppercase-util.util';
import { CapTableTab, Column } from '../fundraising.types';
import { EquityPoolsRow, TabProps, TotalRow } from '../table-components/index';
import { ShareClassTableItem } from './share-class-item';

export const ShareClassesTab: FC<TabProps> = ({ visibleColumns, viewBy, scenarioId }) => {
  const { messagesLocale } = useLocale();
  const { data } = ScenarioHooks.useCapTableShareClasses({ includeSearch: true });
  const { data: summarizeData } = ScenarioHooks.useCapTableSummarize();
  const { modifiedShareClasses } = ScenarioHooks.useModifiedShareClasses({ scenarioId });
  const modifiedShareClassesIds =
    modifiedShareClasses?.map(({ shareClass }) => shareClass.id) || [];

  if (!summarizeData) return;
  const {
    avgSharePrice,
    convertedBasis,
    convertedBasisPercentage,
    diluted,
    dilutedPercentage,
    investment,
    issued,
    issuedPercentage,
    votingPercentage,
    voting,
    companyPools,
    scenarioPools,
  } = summarizeData;

  return (
    <>
      {data?.map(
        (
          {
            stakeholders,
            avgSharePrice,
            convertedBasis,
            convertedBasisPercentage,
            diluted,
            dilutedPercentage,
            investment,
            issued,
            issuedPercentage,
            votingPercentage,
            name,
            voting,
            shareClass,
          },
          i,
        ) => {
          return (
            <ShareClassTableItem
              fields={{
                [Column.NAME]: { value: name, change: null },
                [Column.NO_OF_STAKEHOLDERS]: {
                  value: stakeholders.length,
                  change: null,
                },
                [Column.ISSUED]: {
                  change: null,
                  value: issuedPercentage * 100,
                },
                [Column.DILUTED]: {
                  value: dilutedPercentage * 100,
                  change: null,
                },
                [Column.VOTING]: {
                  value: votingPercentage * 100,
                  change: null,
                },
                [Column.CONVERTED_PERCENTAGE]: {
                  value: convertedBasisPercentage * 100,
                  change: null,
                },
                [Column.NO_ISSUED]: {
                  change: null,
                  value: issued,
                },
                [Column.DILUTED_SHARES]: {
                  change: null,
                  value: diluted,
                },

                [Column.VOTING_RIGHTS]: {
                  change: null,
                  value: voting,
                },
                [Column.INVESTMENT]: {
                  change: null,
                  value: investment,
                },
                [Column.AVERAGE_SHARE_PRICE]: {
                  change: null,
                  value: avgSharePrice,
                },
                [Column.CONVERTED_SHARES]: {
                  change: null,
                  value: convertedBasis,
                },
              }}
              isModified={modifiedShareClassesIds.includes(shareClass?.id)}
              items={stakeholders}
              key={`${name}_${i}`}
              visibleColumns={visibleColumns}
            />
          );
        },
      )}
      <EquityPoolsRow
        fields={{
          [Column.NO_OF_STAKEHOLDERS]: { value: '-', change: null },
          [Column.NAME]: {
            value: messagesLocale['EQUITY_POOLS_GRANTABLE'],
            change: null,
          },
          [Column.ISSUED]: {
            value: toRound(
              calcTotal(companyPools, ({ issuedPercentage }) => issuedPercentage.value) * 100,
            ),
            change: null,
          },
          [Column.DILUTED]: {
            value: toRound(
              calcTotal(companyPools, ({ dilutedPercentage }) => dilutedPercentage.value) * 100,
            ),
            change: null,
          },
          [Column.NO_ISSUED]: {
            change: null,
            value: calcTotal(companyPools, ({ issued }) => issued.value),
          },
          [Column.DILUTED_SHARES]: {
            change: null,
            value: calcTotal(companyPools, ({ diluted }) => diluted.value),
          },
          [Column.VOTING]: { value: '-', change: null },
          [Column.INVESTMENT]: { value: '-', change: null },
          [Column.AVERAGE_SHARE_PRICE]: { value: '-', change: null },
          [Column.VOTING_RIGHTS]: { value: '-', change: null },
          [Column.CONVERTED_SHARES]: {
            change: null,
            value: calcTotal(companyPools, ({ convertedBasis }) => convertedBasis.value),
          },
          [Column.CONVERTED_PERCENTAGE]: {
            change: null,
            value: toRound(
              calcTotal(
                companyPools,
                ({ convertedBasisPercentage }) => convertedBasisPercentage.value,
              ) * 100,
            ),
          },
        }}
        items={companyPools}
        tab={CapTableTab.BY_SHARE_CLASS}
        viewBy={viewBy}
        visibleColumns={visibleColumns}
      />
      <EquityPoolsRow
        fields={{
          [Column.NO_OF_STAKEHOLDERS]: { value: '-', change: null },
          [Column.NAME]: { value: 'Equity Pools Granted', change: null },
          [Column.ISSUED]: {
            value: toRound(
              calcTotal(companyPools, ({ unvestedPercentage }) => unvestedPercentage?.value || 0) *
                100,
            ),
            change: null,
          },
          [Column.DILUTED]: {
            value: 0,
            change: null,
          },
          [Column.NO_ISSUED]: {
            change: null,
            value: calcTotal(companyPools, ({ unvested }) => unvested?.value || 0),
          },
          [Column.DILUTED_SHARES]: {
            change: null,
            value: 0,
          },
          [Column.CONVERTED_SHARES]: {
            change: null,
            value: 0,
          },
          [Column.VOTING]: { value: '-', change: null },
          [Column.INVESTMENT]: { value: '-', change: null },
          [Column.AVERAGE_SHARE_PRICE]: { value: '-', change: null },
          [Column.VOTING_RIGHTS]: { value: '-', change: null },

          [Column.CONVERTED_PERCENTAGE]: {
            change: null,
            value: 0,
          },
        }}
        items={companyPools.map((pool) => ({
          ...pool,
          ...getScenarioGrantedPoolFigures(pool),
        }))}
        tab={CapTableTab.BY_SHARE_CLASS}
        viewBy={viewBy}
        visibleColumns={visibleColumns}
      />

      <EquityPoolsRow
        fields={{
          [Column.NO_OF_STAKEHOLDERS]: { value: '-', change: null },
          [Column.NAME]: {
            value: toUpperCaseWords(messagesLocale['NEW_POOLS']) || '',
            change: null,
          },
          [Column.ISSUED]: {
            value: toRound(
              calcTotal(scenarioPools, ({ issuedPercentage }) => issuedPercentage.value) * 100,
            ),
            change: null,
          },
          [Column.DILUTED]: {
            value: toRound(
              calcTotal(scenarioPools, ({ dilutedPercentage }) => dilutedPercentage.value) * 100,
            ),
            change: null,
          },
          [Column.NO_ISSUED]: {
            change: null,
            value: calcTotal(scenarioPools, ({ issued }) => issued.value),
          },
          [Column.DILUTED_SHARES]: {
            change: null,
            value: calcTotal(scenarioPools, ({ diluted }) => diluted.value),
          },
          [Column.VOTING]: { value: '-', change: null },
          [Column.INVESTMENT]: { value: '-', change: null },
          [Column.AVERAGE_SHARE_PRICE]: { value: '-', change: null },
          [Column.VOTING_RIGHTS]: { value: '-', change: null },
          [Column.CONVERTED_SHARES]: {
            change: null,
            value: calcTotal(scenarioPools, ({ convertedBasis }) => convertedBasis.value),
          },
          [Column.CONVERTED_PERCENTAGE]: {
            change: null,
            value: toRound(
              calcTotal(
                scenarioPools,
                ({ convertedBasisPercentage }) => convertedBasisPercentage.value,
              ) * 100,
            ),
          },
        }}
        items={scenarioPools}
        tab={CapTableTab.BY_SHARE_CLASS}
        trClassName="bg-sunbeam-50"
        viewBy={viewBy}
        visibleColumns={visibleColumns}
      />

      <TotalRow
        fields={{
          [Column.NAME]: { value: messagesLocale['TOTAL'], change: null },
          [Column.ISSUED]: {
            change: null,
            value: issuedPercentage * 100,
          },
          [Column.DILUTED]: {
            value: dilutedPercentage * 100,
            change: null,
          },
          [Column.VOTING]: {
            value: votingPercentage * 100,
            change: null,
          },
          [Column.CONVERTED_PERCENTAGE]: {
            value: convertedBasisPercentage * 100,
            change: null,
          },
          [Column.NO_ISSUED]: {
            change: null,
            value: issued,
          },
          [Column.DILUTED_SHARES]: {
            change: null,
            value: diluted,
          },

          [Column.VOTING_RIGHTS]: {
            change: null,
            value: voting,
          },
          [Column.INVESTMENT]: {
            change: null,
            value: investment,
          },
          [Column.AVERAGE_SHARE_PRICE]: {
            change: null,
            value: avgSharePrice,
          },
          [Column.NO_OF_STAKEHOLDERS]: {
            value: data?.reduce((acc, { stakeholders }) => acc + stakeholders.length, 0) || 0,
            change: null,
          },

          [Column.CONVERTED_SHARES]: {
            change: null,
            value: convertedBasis,
          },
        }}
        tab={CapTableTab.BY_SHARE_CLASS}
        visibleColumns={visibleColumns}
      />
    </>
  );
};
